/**
 * Biocide Service
 * ZeroPath — Registrul Național al Produselor Biocide
 *
 * Colecție Firestore: `biocide` (globală, nu org-scoped)
 * Sursa datelor: Registrul MS, populat via scripts/seed-biocide.js
 *
 * v4.15.2: updateBiocid — câmp clasificareSpaulding în allowed.
 * v4.15: încărcare completă într-un singur getDocs; sortare stabilă msId.
 * Filtrele search/status sunt în UI (memorie). Parametrul filters e rezervat
 * pentru viitor (ex. tipProdus server-side) — ignorat în acest PR.
 */

import { db } from '../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

const COLLECTION = 'biocide';

function sortKeyMsId(row) {
  const v = row.msId;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = parseInt(String(v), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Încarcă toate documentele din colecția biocide, sortate după msId crescător.
 * @param {object} [_filtersReserved] — viitor: { tipProdus }; ignorat în v4.15
 * @returns {{ items: array }}
 */
export async function getBiocide(_filtersReserved = {}) {
  try {
    const snap = await getDocs(collection(db, COLLECTION));
    const items = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => sortKeyMsId(a) - sortKeyMsId(b));
    return { items };
  } catch (err) {
    console.error('[biocideService] getBiocide:', err);
    throw err;
  }
}

/**
 * Obține un biocid după ID Firestore.
 */
export async function getBiocidById(id) {
  try {
    const snap = await getDoc(doc(db, COLLECTION, id));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    console.error('[biocideService] getBiocidById:', err);
    throw err;
  }
}

/**
 * Actualizează câmpurile editabile ale unui biocid.
 */
export async function updateBiocid(id, data, userId) {
  try {
    const allowed = [
      'nrAviz',
      'denumireComerciale',
      'producator',
      'taraProducator',
      'tipProdus',
      'tipProdusLabel',
      'dataInregistrarii',
      'dataExpirarii',
      'status',
      'substanteActive',
      'clasificareSpaulding'
    ];
    const filtered = Object.fromEntries(
      Object.entries(data).filter(([k]) => allowed.includes(k))
    );

    await updateDoc(doc(db, COLLECTION, id), {
      ...filtered,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    });
  } catch (err) {
    console.error('[biocideService] updateBiocid:', err);
    throw err;
  }
}

/**
 * Șterge un biocid după ID.
 */
export async function deleteBiocid(id) {
  try {
    await deleteDoc(doc(db, COLLECTION, id));
  } catch (err) {
    console.error('[biocideService] deleteBiocid:', err);
    throw err;
  }
}

export const biocideService = {
  getBiocide,
  getBiocidById,
  updateBiocid,
  deleteBiocid
};

export default biocideService;
